import { type ModelConfig } from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';

import { CreateConfigModal } from '@/components/eval/CreateConfigModal';
import { EditConfigModal } from '@/components/eval/EditConfigModal';
import { EvalLayout } from '@/components/eval/EvalLayout';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

export default function ConfigsPage() {
  const [configs, setConfigs] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<ModelConfig | null>(
    null
  );
  const [editingConfig, setEditingConfig] = useState<ModelConfig | null>(null);
  const [enabledFilter, setEnabledFilter] = useState<
    'all' | 'enabled' | 'disabled'
  >('all');

  useEffect(() => {
    loadConfigs();
  }, []);

  async function loadConfigs() {
    setLoading(true);
    try {
      const data = await apiRequest('/api/eval/configs');
      setConfigs(data.configs || []);
    } catch (e) {
      logError('Error loading configs', e);
    } finally {
      setLoading(false);
    }
  }

  // Filter configs based on enabled status
  const filteredConfigs = configs.filter((config: any) => {
    if (enabledFilter === 'enabled') {
      return config.enabled !== false;
    } else if (enabledFilter === 'disabled') {
      return config.enabled === false;
    }
    return true; // "all"
  });

  return (
    <EvalLayout>
      <div>
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1
              className="font-serif text-3xl font-medium m-0"
              style={{ color: '#1A1A1A' }}
            >
              Model Configurations
            </h1>
            <p className="mt-2 text-sm" style={{ color: '#6B6B6B' }}>
              Saved model settings for systematic testing. Test the same model
              with different parameters.
            </p>
          </div>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + Create Config
          </Button>
        </header>

        {/* Filter Controls */}
        <div className="flex items-center gap-2 mb-6">
          <span
            className="text-sm font-medium mr-2"
            style={{ color: '#1A1A1A' }}
          >
            Filter:
          </span>
          {(['all', 'enabled', 'disabled'] as const).map(filter => {
            const isActive = enabledFilter === filter;
            const count =
              filter === 'all'
                ? configs.length
                : filter === 'enabled'
                  ? configs.filter((c: any) => c.enabled !== false).length
                  : configs.filter((c: any) => c.enabled === false).length;
            const label = filter.charAt(0).toUpperCase() + filter.slice(1);
            return (
              <button
                key={filter}
                onClick={() => setEnabledFilter(filter)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                style={{
                  background: isActive ? '#8B2332' : 'white',
                  color: isActive ? 'white' : '#1A1A1A',
                  border: `1px solid ${isActive ? '#8B2332' : '#E8E0D8'}`,
                }}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-16" style={{ color: '#A0A0A0' }}>
            Loading configurations...
          </div>
        ) : filteredConfigs.length === 0 ? (
          <div
            className="bg-white rounded-xl text-center py-16 px-8"
            style={{ border: '1px solid #E8E0D8' }}
          >
            <p className="m-0" style={{ color: '#A0A0A0' }}>
              {configs.length === 0
                ? 'No configurations found. Create your first config to get started.'
                : `No ${enabledFilter} configurations found.`}
            </p>
          </div>
        ) : (
          <div
            className="grid gap-6"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            }}
          >
            {filteredConfigs.map(config => (
              <div
                key={config.id}
                onClick={() => setSelectedConfig(config)}
                className="bg-white rounded-xl p-5 cursor-pointer transition-all duration-150"
                style={{ border: '1px solid #E8E0D8' }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = '#8B2332';
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(139, 35, 50, 0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#E8E0D8';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: '#1A1A1A' }}
                >
                  {config.name}
                </h3>

                <div className="mb-4">
                  <div className="text-sm mb-2" style={{ color: '#6B6B6B' }}>
                    <strong style={{ color: '#1A1A1A' }}>Model:</strong>{' '}
                    {config.provider}/{config.api_model}
                  </div>
                  <div
                    className="flex gap-4 text-sm"
                    style={{ color: '#6B6B6B' }}
                  >
                    <span>
                      <strong style={{ color: '#1A1A1A' }}>Temp:</strong>{' '}
                      {config.temperature ?? 'default'}
                    </span>
                    <span>
                      <strong style={{ color: '#1A1A1A' }}>Tokens:</strong>{' '}
                      {config.max_tokens ?? 'default'}
                    </span>
                  </div>
                </div>

                <div
                  className="text-xs pt-3"
                  style={{ color: '#A0A0A0', borderTop: '1px solid #E8E0D8' }}
                >
                  Created {new Date(config.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreateModal && (
          <CreateConfigModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              loadConfigs();
            }}
          />
        )}

        {selectedConfig && (
          <Modal
            title={selectedConfig.name}
            subtitle="Configuration Details"
            onClose={() => setSelectedConfig(null)}
            maxWidth="500px"
          >
            <div className="grid gap-4">
              <div>
                <label
                  className="text-sm font-medium block mb-2"
                  style={{ color: '#1A1A1A' }}
                >
                  Provider
                </label>
                <div
                  className="p-3 rounded-lg"
                  style={{ background: '#F5F0EB', color: '#1A1A1A' }}
                >
                  {selectedConfig.provider}
                </div>
              </div>

              <div>
                <label
                  className="text-sm font-medium block mb-2"
                  style={{ color: '#1A1A1A' }}
                >
                  API Model
                </label>
                <div
                  className="p-3 rounded-lg"
                  style={{ background: '#F5F0EB', color: '#1A1A1A' }}
                >
                  {selectedConfig.api_model}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="text-sm font-medium block mb-2"
                    style={{ color: '#1A1A1A' }}
                  >
                    Temperature
                  </label>
                  <div
                    className="p-3 rounded-lg"
                    style={{ background: '#F5F0EB', color: '#1A1A1A' }}
                  >
                    {selectedConfig.temperature ?? 'default'}
                  </div>
                </div>

                <div>
                  <label
                    className="text-sm font-medium block mb-2"
                    style={{ color: '#1A1A1A' }}
                  >
                    Max Tokens
                  </label>
                  <div
                    className="p-3 rounded-lg"
                    style={{ background: '#F5F0EB', color: '#1A1A1A' }}
                  >
                    {selectedConfig.max_tokens ?? 'default'}
                  </div>
                </div>
              </div>
            </div>

            <div
              className="flex justify-between items-center pt-4 mt-6"
              style={{ borderTop: '1px solid #E8E0D8' }}
            >
              <span className="text-xs" style={{ color: '#A0A0A0' }}>
                Created {new Date(selectedConfig.created_at).toLocaleString()}
              </span>
              <Button
                variant="primary"
                size="small"
                onClick={() => setEditingConfig(selectedConfig)}
              >
                Edit
              </Button>
            </div>
          </Modal>
        )}

        {/* Edit Modal */}
        {editingConfig && (
          <EditConfigModal
            config={editingConfig}
            onClose={() => setEditingConfig(null)}
            onSuccess={() => {
              setEditingConfig(null);
              setSelectedConfig(null);
              loadConfigs();
            }}
          />
        )}
      </div>
    </EvalLayout>
  );
}
